import { mergeClasses } from "../../../../helpers/styling"
import { TreeToggleConfig } from "../config-schema"
import { SizeVariantContext, ToolButton } from "../../../../shared/action-buttons/action-button-primitives"
import { useWidgetColorsCssVars } from "../../../styling/use-widget-styling"

const DEFAULT_ICON_ON = "mui-check-box"
const DEFAULT_ICON_OFF = "mui-check-box-outline-blank"

/**
 * One per-row toggle, rendered among the action buttons.
 * Its state lives in the tree store, so it needs no template expression to know
 * whether it is on - unlike an action button, which can only interpolate strings.
 *
 * With only iconOn configured, the same icon is reused for the off state with a slash
 * drawn through it. Handy for icon sets that have no "-off" variant: DynamicIcon
 * offers lucide-ghost, for instance, but no lucide-ghost-off.
 */
export function TreeNodeToggle(props: {
  toggle: TreeToggleConfig
  toggled: boolean
  variant: "default" | "medium" | "compact"
  onToggle: (toggled: boolean) => void
}) {
  const { iconOn, iconOff, colorOn, colorOff, tooltipOn, tooltipOff } = props.toggle
  const color = props.toggled ? colorOn : colorOff
  const colorsCssVars = useWidgetColorsCssVars()

  const slashed = !props.toggled && !iconOff && Boolean(iconOn)
  const icon = props.toggled
    ? iconOn || DEFAULT_ICON_ON
    : iconOff || iconOn || DEFAULT_ICON_OFF

  return (
    <div style={colorsCssVars} className="flex flex-shrink-0">
      <SizeVariantContext.Provider value={props.variant}>
        <ToolButton
          role="checkbox"
          aria-checked={props.toggled}
          aria-label={props.toggle.id}
          title={(props.toggled ? tooltipOn : tooltipOff) ?? undefined}
          icon={icon}
          style={{ color: color ?? undefined }}
          animated
          onClick={e => {
            e.stopPropagation() // don't trigger selection
            props.onToggle(!props.toggled)
          }}
          className={mergeClasses("relative justify-center", {
            "text-wistor-alt": props.toggled && !colorOn,
            "opacity-60 hover:opacity-100": !props.toggled,
          })}>
          {slashed && (
            <span aria-hidden
                  className="absolute inset-0 flex items-center justify-center">
              <span className="w-[80%] h-[1.5px] rotate-45 bg-current rounded-full" />
            </span>
          )}
        </ToolButton>
      </SizeVariantContext.Provider>
    </div>
  )
}
